import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { AuthOAuthCallbackMount } from "@/components/lembra/AuthOAuthCallbackMount";
import { ProtectedRoute } from "@/components/lembra/ProtectedRoute";
import { BakeryProtectedRoute } from "@/components/lembra/BakeryProtectedRoute";
import { AdminProtectedRoute } from "@/components/lembra/AdminProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AppErrorBoundary } from "@/components/lembra/AppErrorBoundary";

// MS45: AppLayout retired. ConciergeLayout owns the chrome for the
// entire authed customer portal (Dashboard + every other page).
import { ConciergeLayout } from "@/components/lembra/ConciergeLayout";
import { FloatingConciergeProvider } from "@/contexts/FloatingConciergeContext";
import { AuthedChatGate } from "@/components/concierge/AuthedChatGate";
import { BakeryLayout } from "@/components/lembra/BakeryLayout";
import { AdminLayout } from "./pages/admin/AdminLayout";

// Landing stays eager, it's the most-hit route and we don't want a Suspense
// flash on the homepage. NotFound is tiny and used as the catch-all so we
// keep it eager too. Everything else is lazy-loaded so a first-time visitor
// to "/" doesn't download bytes for the customer portal, bakery portal,
// admin tooling, blog editor, etc. Each portal forms a natural async chunk.
import Landing from "./pages/Index";
import NotFound from "./pages/NotFound";
// MS392: eager (was lazy). See note below the lazy list.
import Login from "./pages/Login";
// P1a: eager-import ConciergeLayout tab routes so first sidebar click
// doesn't pay a lazy-chunk Suspense flash. Prefetch hook in ConciergeLayout
// still warms React Query caches; eager import removes the JS chunk delay.
import Dashboard from "./pages/Dashboard";
import Team from "./pages/Team";
import Calendar from "./pages/Calendar";
import Orders from "./pages/Orders";
import Notifications from "./pages/Notifications";
import SettingsPage from "./pages/Settings";
import Refer from "./pages/Refer";
import Wishlist from "./pages/Wishlist";

// Public pages, small, lazy chunks so the landing JS payload stays minimal.
// MS392: Login moved out of the lazy bundle into the eager imports above
// (see line 27). Audit measured a ~5s blank-to-content gap on first /login
// visit because the chunk fetch + zod/react-hook-form/form deps were
// cold-loaded after the route mounted. /login is the critical-path
// destination for every unauthed visitor on celebrations.lembra.com.au,
// so eager-loading it removes the Suspense delay at the cost of a few
// KB on the initial bundle (deps already overlap with Signup/Settings,
// so the marginal hit is small).
const Signup = lazy(() => import("./pages/Signup"));
// MS496: Order lazy import removed — the /order route now redirects
// to /chat via <Navigate />, so the legacy builder component is no
// longer mounted from App.tsx. File kept on disk in case the route
// needs a temporary revert; chunk drops out of the lazy bundle.
const Feedback = lazy(() => import("./pages/Feedback"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const PartnerApply = lazy(() => import("./pages/PartnerApply"));
// MS21: /brand — public design-system + voice page. Useful for
// vendors / contractors / contributors writing copy or building
// surfaces that need to match Lembra's voice.
const Brand = lazy(() => import("./pages/Brand"));
// MS26: /vendors/:slug — per-vendor landing pages so each vendor
// has a stable URL they can share, and so search engines have a
// per-vendor SEO surface to index.
const Vendor = lazy(() => import("./pages/Vendor"));
// MS29: /vendors — directory index. Lists all active vendors with
// a category-filter chip row, links to individual /vendors/<slug>
// pages.
const Vendors = lazy(() => import("./pages/Vendors"));
// MS30: /pricing — public pricing page. Reverses the S6a "no
// pricing on landing" decision but in its own surface, framed
// open-ended (standard from $99 + concierge for bigger) so it
// doesn't anchor users to rigid tiers.
const Pricing = lazy(() => import("./pages/Pricing"));
// MS31: /calculator — interactive ROI tool. Team size + occasion
// toggles → annual cost + hours saved. Single most effective B2B
// sales surface for prospects who need to justify spend.
const Calculator = lazy(() => import("./pages/Calculator"));
// MS33: /security — public trust page. Where data lives, how
// Stripe handles payments, what employee data we collect (and
// don't). B2B essential.
const Security = lazy(() => import("./pages/Security"));
// MS34: /our-team — public founders page. Humanises the brand
// for B2B buyers who want to see who's behind the product.
// Note: lives at /our-team (NOT /team) because /team is the
// authed roster surface for logged-in customers, established
// long before this page existed.
const OurTeam = lazy(() => import("./pages/OurTeam"));
// MS39: /changelog — hand-curated release notes grouped by
// month. Answers "are they actually shipping?" without us
// having to claim it. Pulled ahead of MS35 (which needed
// substrate we don't have yet).
const Changelog = lazy(() => import("./pages/Changelog"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const BakeryLogin = lazy(() => import("./pages/BakeryLogin"));
const BakerySignup = lazy(() => import("./pages/BakerySignup"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Activate = lazy(() => import("./pages/Activate"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
// Sprint 1.5/1.6/1.7c/1.7h, chat + browse pivots are public-facing too
// (anon-friendly via guest_session_id pattern).
const Chat = lazy(() => import("./pages/Chat"));
const Browse = lazy(() => import("./pages/BrowseWithConcierge"));
const ConciergeWizard = lazy(() => import("./pages/ConciergeWizard"));
const OrderConcierge = lazy(() => import("./pages/OrderConcierge"));
// Sprint 2.3a, public gift-share viewer.
const GiftShare = lazy(() => import("./pages/GiftShare"));

// Customer portal, only needed once a user logs in.
const Onboarding = lazy(() => import("./pages/Onboarding"));
// Dashboard, Team, Calendar, Orders, Notifications, Settings, Refer,
// Wishlist eager-imported above (P1a instant tab load).
const OrderCreate = lazy(() => import("./pages/OrderCreate"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const OrderSuccessCelebrate = lazy(() => import("./pages/OrderSuccessCelebrate"));
const Billing = lazy(() => import("./pages/Billing"));
const SettingsLayout = lazy(() =>
  import("./components/settings/SettingsLayout").then((m) => ({
    default: m.SettingsLayout,
  })),
);
// Sprint 1.2, rich profile route.
const TeamMemberProfile = lazy(() => import("./pages/TeamMemberProfile"));

// Bakery partner portal, only loaded for partner accounts.
const BakeryDashboard = lazy(() => import("./pages/bakery/BakeryDashboard"));
const BakeryOrders = lazy(() => import("./pages/bakery/BakeryOrders"));
const BakeryProfile = lazy(() => import("./pages/bakery/BakeryProfile"));
const BakerySettings = lazy(() => import("./pages/bakery/BakerySettings"));
const BakeryCatalogue = lazy(() => import("./pages/bakery/BakeryCatalogue"));
const BakeryAvailability = lazy(() => import("./pages/bakery/BakeryAvailability"));
const BakeryStats = lazy(() => import("./pages/bakery/BakeryStats"));

// Admin portal, heaviest chunk (blog editor pulls in markdown deps).
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminApplications = lazy(() => import("./pages/admin/AdminApplications"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminCompanies = lazy(() => import("./pages/admin/AdminCompanies"));
const AdminCompanyTeam = lazy(() => import("./pages/admin/AdminCompanyTeam"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminNotices = lazy(() => import("./pages/admin/AdminNotices"));
const AdminBlogPosts = lazy(() => import("./pages/admin/AdminBlogPosts"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminBlogAuthors = lazy(() => import("./pages/admin/AdminBlogAuthors"));
const AdminBlogCategories = lazy(() => import("./pages/admin/AdminBlogCategories"));
// Sprint 1.7j/1.7l/1.7m/1.7p/2.1b/2.1d, admin tooling for the
// chat/browse-originated multi-vendor flow.
const AdminGiftRequests = lazy(() => import("./pages/admin/AdminGiftRequests"));
const AdminFunnel = lazy(() => import("./pages/admin/AdminFunnel"));
const AdminVendors = lazy(() => import("./pages/admin/AdminVendors"));
const AdminVendorDetail = lazy(() => import("./pages/admin/AdminVendorDetail"));
const AdminCatalogueImagery = lazy(() => import("./pages/admin/AdminCatalogueImagery"));
const AdminCatalogueTiers = lazy(() => import("./pages/admin/AdminCatalogueTiers"));
const AdminChatTranscript = lazy(() => import("./pages/admin/AdminChatTranscript"));

// Lightweight fallback used while a lazy chunk is being fetched.
// Matches ProtectedRoute's spinner so the transition feels native.
const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-cream">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
  </div>
);

// React Query defaults, tuned per docs/decisions/0006 (conventions).
// staleTime 30s: switching between pages feels instant for the common case,
// but data never gets older than 30s before a background refetch refreshes it.
// refetchOnWindowFocus: cheap correctness win if the user comes back from
// another tab. Per-query overrides are encouraged (fast-moving feeds drop to
// 10s, static catalogues climb to 5m), see src/hooks/queries/*.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// Default social meta, applies to every route.
//
// Why here and not in index.html: the static HTML shell can hold at most
// one og:image, but per-page Helmets (Blog.tsx, BlogSEOHead.tsx) need to
// override the default with a route-specific image. If both the static
// tag and a Helmet tag exist, crawlers like Facebook take the first one
// (the homepage default) and ignore the per-post override. Owning the
// defaults from a Helmet in App.tsx lets per-page Helmets replace them
// cleanly via Helmet's "deepest-wins" deduplication, exactly one
// og:image per pre-rendered page, with the right one winning.
// MS178: copy refresh. The previous description ("a corporate
// celebration automation service for Melbourne-based startups. We
// deliver cakes, personalised cards, and celebration packages.")
// was written in the cakes-only era and contradicted the multi-
// vendor positioning the rest of the site has used since the
// concierge pivot. Aligned with the homepage PageMeta description
// in Index.tsx so one canonical sentence echoes across the default
// + homepage social meta. "Corporate" → "workplace" matches the
// site's current vocabulary; the og:image:alt already used it.
const DEFAULT_SOCIAL_DESC =
  "Lembra remembers every workplace celebration. Tell our concierge who's celebrating; we curate cakes, hampers, flowers, vouchers and more from Melbourne's best vendors and handle delivery. Pay per gift, no subscription.";
const DEFAULT_SOCIAL_TITLE =
  "Lembra remembers every workplace celebration, so you don't have to.";

const DefaultSocialMeta = () => (
  <Helmet>
    <meta name="description" content={DEFAULT_SOCIAL_DESC} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Lembra" />
    <meta property="og:title" content={DEFAULT_SOCIAL_TITLE} />
    <meta property="og:description" content={DEFAULT_SOCIAL_DESC} />
    <meta property="og:image" content="https://lembra.com.au/og-default.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:image:alt" content="Lembra, workplace celebrations, sorted. Calendar showing the fortnight ahead with birthdays, anniversaries, and milestones highlighted." />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={DEFAULT_SOCIAL_TITLE} />
    <meta name="twitter:description" content={DEFAULT_SOCIAL_DESC} />
    <meta name="twitter:image" content="https://lembra.com.au/og-default.png" />
    <meta name="twitter:image:alt" content="Lembra, workplace celebrations, sorted. Calendar showing the fortnight ahead with birthdays, anniversaries, and milestones highlighted." />
  </Helmet>
);

// /catering safety net. Lovable's static host doesn't honor public/_redirects,
// so the clean URL would otherwise fall through to the SPA's NotFound. All
// internal links already point at https://catering.lembra.com.au directly; this catches anyone
// who types the bare URL, lands from an external link, or has a stale
// bookmark. window.location.replace (not React Router Navigate) is required
// because https://catering.lembra.com.au is a static file outside the SPA, Navigate would
// stay inside React Router and 404 again.
const CateringRedirect = () => {
  useEffect(() => {
    window.location.replace("https://catering.lembra.com.au");
  }, []);
  return null;
};

// Sprint 2.3b v0, captures `?ref=CODE` from the visitor's URL into
// localStorage on first mount. First-touch attribution; the stored
// code is read by record_referral_signup at signup time. Renders
// nothing; pure side effect.
const ReferralCaptureMount = () => {
  useEffect(() => {
    void import("@/lib/referralCapture").then((m) => m.captureReferralFromUrl());
  }, []);
  return null;
};

// Sprint 2.3b v0, links a stored ?ref= code to the current user
// the first time auth resolves with a session. The signup flow
// requires email confirmation so we can't fire this in /signup
// (no auth.uid() yet), has to wait until the user lands somewhere
// authenticated. Idempotent server-side: record_referral_signup
// silently returns the existing referral row id if the user has
// already been linked, so re-firings are cheap. localStorage code
// is cleared on first successful link so subsequent visits don't
// re-attribute.
const ReferralLinkOnSignIn = () => {
  // Imported lazily to keep referralCapture out of the cold-start
  // bundle, first visit doesn't need it until they sign up.
  useEffect(() => {
    let cancelled = false;
    let unlistened = false;
    void Promise.all([
      import("@/integrations/supabase/client"),
      import("@/lib/referralCapture"),
    ]).then(async ([{ supabase }, { readStoredReferralCode, clearStoredReferralCode }]) => {
      if (cancelled) return;
      const tryLink = async (userId: string | null | undefined) => {
        if (!userId) return;
        const code = readStoredReferralCode();
        if (!code) return;
        const { error } = await supabase.rpc("record_referral_signup", { p_code: code });
        // Whether the RPC linked, no-op'd, or silently returned NULL
        // for a stale code, we clear the stored value so it doesn't
        // re-fire on every session change. The capture runs again
        // only if the visitor lands with a fresh ?ref= param.
        if (!error) clearStoredReferralCode();
      };
      // Try once on mount in case there's already a session.
      const { data: { session } } = await supabase.auth.getSession();
      await tryLink(session?.user?.id ?? null);
      // Listen for future session changes (e.g. magic-link confirm).
      const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
        if (unlistened) return;
        void tryLink(sess?.user?.id ?? null);
      });
      // Return cleanup
      return () => {
        unlistened = true;
        sub.subscription.unsubscribe();
      };
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
};

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <DefaultSocialMeta />
        <BrowserRouter>
          <ScrollToTop />
          <ReferralCaptureMount />
          <ReferralLinkOnSignIn />
          <AuthProvider>
            <AuthOAuthCallbackMount />
            <Suspense fallback={<RouteFallback />}>
            {/* MS356: page-level safe-area-bottom padding so iOS Safari's
                translucent URL bar + Android Chrome's tab strip don't
                overlap the final section of any route. Previously only
                Index.tsx wrapped itself in this — every other page
                bled content under the browser chrome on mobile.
                Lifted here as the canonical wrapper so every route
                inherits without per-page boilerplate. Sticky/fixed
                bottom elements (FloatingConcierge button, Order page
                price bar) handle their own offsets in addition. */}
            <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <Routes>
            {/* ── Public routes ── */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sign-in" element={<Navigate to="/login" replace />} />
            <Route path="/signup" element={<Signup />} />
            {/* MS496: legacy /order builder retired. Every Send-a-gift CTA
                on the platform now goes to /chat (the inline concierge):
                landing nav, mobile menu, FinalCTA, HowLembraWorks, vendor
                profile pages, CmdK. Redirect /order → /chat as a backstop
                so old bookmarks, external links, and stale email CTAs
                still land in the new flow rather than the deprecated
                builder. /order/success stays on its own route — it's the
                post-checkout page Stripe redirects to (Sprint 2.5a). */}
            <Route path="/order" element={<Navigate to="/chat" replace />} />
            <Route path="/order/success" element={<OrderSuccess />} />
            {/* MS4 cleanup: /pricing route deleted. The pricing
                section was permanently removed from Landing in
                S6a (Lucky's call: don't anchor users to fixed
                tiers; pricing surfaces in the order/chat flow at
                the point of intent). Visiting /pricing now
                gracefully falls through to the catch-all NotFound
                route below, better signal than the silent
                "you're on the landing but the section is gone"
                state we had before. */}
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/partner-apply" element={<PartnerApply />} />
            <Route path="/brand" element={<Brand />} />
            <Route path="/vendors" element={<Vendors />} />
            <Route path="/vendors/:slug" element={<Vendor />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/security" element={<Security />} />
            <Route path="/our-team" element={<OurTeam />} />
            <Route path="/changelog" element={<Changelog />} />
            {/* /catering -> https://catering.lembra.com.au safety net. See CateringRedirect above. */}
            <Route path="/catering" element={<CateringRedirect />} />
            <Route path="/bakery-login" element={<BakeryLogin />} />
            <Route path="/bakery-signup" element={<BakerySignup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* /activate is the redirect target for admin-led invite emails
                (supabase.auth.admin.generateLink type='invite'). Public
                route, by the time the user lands here, the SDK has already
                exchanged the invite token for a session, so the page just
                needs to collect a password and route to /dashboard. */}
            <Route path="/activate" element={<Activate />} />
            {/* /welcome is the Supabase confirm-signup redirect target.
                Public route (no ProtectedRoute) so the fresh user lands
                here after clicking the verification link; the CTA to
                /dashboard bounces them through ProtectedRoute to
                /onboarding on arrival. */}
            <Route path="/welcome" element={<Welcome />} />
            {/* Public preview of the new concierge dashboard. Lets us
                share the design with seeded mock data without requiring
                a session. Dashboard.tsx detects no-user and falls back
                to demo celebrations. Remove once cutover is complete.
                Wrapped in FloatingConciergeProvider — Dashboard consumes
                useFloatingConciergeUi, which lives in ConciergeLayout for
                authed routes but is absent here, so the standalone demo
                route crashed to the error boundary without this. */}
            <Route
              path="/dashboard-demo"
              element={
                <FloatingConciergeProvider>
                  <Dashboard />
                </FloatingConciergeProvider>
              }
            />

            {/* Public preview of the new 3-screen concierge wizard.
                Demo mode skips the DB write and routes through the
                success screen. Real /onboarding stays on the existing
                flow until cutover. */}
            <Route path="/onboarding-demo" element={<ConciergeWizard />} />
            {/* Public preview of the redesigned one-shot guest order
                flow. Replaces the existing /order builder for the
                concierge era, single brief → ApprovalModal → success.
                Live /order route stays on the legacy builder until
                cutover. */}
            <Route path="/order-demo" element={<OrderConcierge />} />
            {/* Sprint 1.7c, /chat is now PUBLIC. Anonymous landing-
                page visitors can open a conversation and complete a
                purchase via Stripe without signing up. The chat-
                concierge edge function distinguishes user vs guest
                callers via Authorization header + a browser-stored
                guest session id. Sat outside ProtectedRoute so cold
                visitors land directly on the chat. Authed visitors
                still hit it; the Chat page detects auth state via
                useAuth and switches modes. */}
            <Route path="/chat" element={<AuthedChatGate><Chat /></AuthedChatGate>} />
            {/* Sprint 1.7h, /browse is now PUBLIC. Same anon pattern
                as /chat, vendor_products + vendor_partners RLS was
                opened to anon, OrderConfirmSheet collects email +
                name in the sheet for guest checkout. */}
            <Route path="/browse" element={<Browse />} />
            {/* Sprint 2.3a, public gift viewer. The buyer copies a
                share link from /order/success or /orders/:id and shares
                wherever (Slack, LinkedIn, in-person). Anyone with the
                token can view a sanitized recipient-safe page. Routed
                here in the public block; access control is the token
                being unguessable + the get-gift-share function only
                returning paid + non-cancelled orders. */}
            <Route path="/gift/:token" element={<GiftShare />} />

            {/* ── Customer portal (authenticated) ──
                MS45: every authed customer route now goes through
                ConciergeLayout (white sidebar + Newsreader logo
                wordmark + Concierge/Settings nav grouping +
                AutoPilot card + sign-out). Dashboard's prior
                bypass-the-layout treatment was the right chrome
                but only on /dashboard; promoting it here unifies
                the whole portal. /onboarding stays unwrapped — its
                stepper IS the page and chrome would compete with
                it. */}
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route element={<ConciergeLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/team" element={<Team />} />
                <Route path="/team/:id/profile" element={<TeamMemberProfile />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/orders/new" element={<OrderCreate />} />
                <Route path="/orders/:id/celebrate" element={<OrderSuccessCelebrate />} />
                <Route path="/orders/:id" element={<OrderDetail />} />
                {/* Sprint 2.2c, Wishlist v1. Authed-only by virtue of
                    living inside the ProtectedRoute wrapper. */}
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<SettingsLayout />}>
                  <Route path="account" element={<SettingsPage />} />
                  <Route path="company" element={<SettingsPage />} />
                  <Route path="program" element={<SettingsPage />} />
                  {/* Legacy program sub-routes → unified program page */}
                  <Route
                    path="celebrations"
                    element={<Navigate to="/settings/program#celebrations" replace />}
                  />
                  <Route
                    path="budget"
                    element={<Navigate to="/settings/program#budget" replace />}
                  />
                  <Route
                    path="tiers"
                    element={<Navigate to="/settings/program#tiers" replace />}
                  />
                  <Route path="payment" element={<SettingsPage />} />
                  <Route path="autopilot" element={<SettingsPage />} />
                  <Route path="notifications" element={<SettingsPage />} />
                </Route>
                {/* MS530: dedicated Refer-a-friend page. Was a SectionCard
                    inside /settings (AUDIT-019); promoted to its own
                    Growth surface so the copy-link CTA can keep its
                    44px touch target without fighting smaller Settings
                    buttons (AUDIT-020). */}
                <Route path="/refer" element={<Refer />} />
              </Route>
            </Route>

            {/* ── Bakery partner portal ── */}
            <Route element={<BakeryProtectedRoute />}>
              <Route element={<BakeryLayout />}>
                <Route path="/bakery/dashboard" element={<BakeryDashboard />} />
                <Route path="/bakery/orders" element={<BakeryOrders />} />
                <Route path="/bakery/catalogue" element={<BakeryCatalogue />} />
                <Route path="/bakery/availability" element={<BakeryAvailability />} />
                <Route path="/bakery/stats" element={<BakeryStats />} />
                <Route path="/bakery/profile" element={<BakeryProfile />} />
                <Route path="/bakery/settings" element={<BakerySettings />} />
              </Route>
            </Route>

            {/* ── Admin portal ── */}
            <Route element={<AdminProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/applications" element={<AdminApplications />} />
                <Route path="/admin/partners" element={<AdminPartners />} />
                <Route path="/admin/companies" element={<AdminCompanies />} />
                {/* Admin-scoped team management, used to pre-seed,
                    fix data, or manage team-member fields on behalf of
                    a customer. Same auth gate as other /admin/* routes
                    (AdminProtectedRoute), and admin RLS on team_members
                    handles the cross-company writes. */}
                <Route path="/admin/companies/:id/team" element={<AdminCompanyTeam />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                {/* MS454: /admin/orders/:id deep link — every "Open in admin"
                    email / Slack alert from edge functions points here.
                    Renders AdminOrders with the target order pre-selected so
                    the detail panel opens immediately. */}
                <Route path="/admin/orders/:orderId" element={<AdminOrders />} />
                {/* Sprint 1.7j, focused triage console for orders
                    originated via the chat / browse / OrderConfirmSheet
                    flow. Filters by cake_size · separator (the marker
                    submit-gift-request writes); independent of the
                    legacy AdminOrders page so it doesn't disturb the
                    existing cake-fulfillment workflow. */}
                <Route path="/admin/gift-requests" element={<AdminGiftRequests />} />
                {/* Sprint 1.7l, funnel attribution dashboard.
                    Reads from interaction_captures, surfaces conversion
                    rates by stage, surface split, and bridge clicks. */}
                <Route path="/admin/funnel" element={<AdminFunnel />} />
                {/* Sprint 2.1b, per-vendor performance list. Differs
                    from /admin/partners (CRUD) by being analytics-only:
                    paid revenue, fulfilment time, last order, etc. */}
                <Route path="/admin/vendors" element={<AdminVendors />} />
                {/* Sprint 2.1d, per-vendor drill-down (profile + recent
                    orders). Click-through from the list row above. */}
                <Route path="/admin/vendors/:slug" element={<AdminVendorDetail />} />
                {/* Sprint 1.7m, catalogue imagery curation tool.
                    Lets ops attach vendor-social-sourced image URLs
                    to vendor_products rows so the customer-facing
                    cards stop rendering placeholder gradients. */}
                <Route path="/admin/catalogue-imagery" element={<AdminCatalogueImagery />} />
                <Route path="/admin/catalogue-tiers" element={<AdminCatalogueTiers />} />
                {/* Sprint 1.7p, read-only chat-transcript viewer.
                    Linked from /admin/gift-requests when an order
                    has chat_session_id (chat-originated orders). */}
                <Route path="/admin/chat/:id" element={<AdminChatTranscript />} />
                {/* Lembra-admin notifications feed. Re-uses the customer
                    Notifications page, it queries the notifications table
                    by `user_id = auth.uid()`, so it works for any role
                    with rows of their own (broadcastNewOrder writes one
                    per `lembra_admin` profile on every confirmed order). */}
                <Route path="/admin/notifications" element={<Notifications />} />
                <Route path="/admin/notices" element={<AdminNotices />} />
                <Route path="/admin/blog" element={<AdminBlogPosts />} />
                <Route path="/admin/blog/new" element={<AdminBlogEditor />} />
                <Route path="/admin/blog/:id/edit" element={<AdminBlogEditor />} />
                <Route path="/admin/blog/authors" element={<AdminBlogAuthors />} />
                <Route path="/admin/blog/categories" element={<AdminBlogCategories />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
            </div>
            </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
